import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

const REASONS = {
  world: {
    title: "Sealed Volume",
    body: "This world is locked to Sovereigns. Ascend to unlock all handcrafted worlds and community publishing.",
  },
  hard_mode: {
    title: "Hard Mode · Sovereign Only",
    body: "Limited undos. Unavoidable encounters. Permadeath. This crucible is reserved for Sovereigns.",
  },
  limit: {
    title: "Preview Ended · 10 Turns",
    body: "Your free preview of this chronicle has ended at 10 turns. Ascend to Sovereign to continue — and to play every world without limit.",
  },
  turn_limit: {
    title: "Preview Ended · 10 Turns",
    body: "Your free preview of this chronicle has ended at 10 turns. Ascend to Sovereign to continue — and to play every world without limit.",
  },
};

export default function PaywallModal({ reason, message, resumeAdventureId, onClose }) {
  const meta = REASONS[reason] || REASONS.world;
  const [tiers, setTiers] = useState([]);
  const [founder, setFounder] = useState(null);
  const [selected, setSelected] = useState("annual");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [t, f] = await Promise.all([
          api.get("/payments/tiers"),
          api.get("/payments/founder-status"),
        ]);
        setTiers(t.data);
        setFounder(f.data);
      } catch (error) {
        // Pricing tiers failed to load — modal falls back to disabled Ascend button;
        // user can retry by closing/reopening.
        if (process.env.NODE_ENV !== "production") console.debug("tiers load error:", error);
      }
    })();
  }, []);

  const claimFounder = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: "sovereign_founder",
        origin_url: window.location.origin,
        resume_adventure_id: resumeAdventureId || null,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not claim");
      setLoading(false);
    }
  };

  const ascend = async () => {
    const tier = tiers.find((t) => t.id === selected);
    if (!tier) return;
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: tier.lookup_key,
        origin_url: window.location.origin,
        resume_adventure_id: resumeAdventureId || null,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center px-4 py-6 overflow-y-auto" onClick={onClose} data-testid="paywall-modal">
      <div className="tome-card max-w-2xl w-full rounded-sm p-6 sm:p-8 space-y-6 my-auto" onClick={(e) => e.stopPropagation()}>
        <div>
          <div className="font-mono text-[0.6rem] uppercase tracking-[0.4em] gold mb-2">Sovereign's Covenant</div>
          <h2 className="font-serif-display text-2xl sm:text-3xl leading-tight">{meta.title}</h2>
          <p className="font-prose text-stone-300 leading-relaxed mt-2">{message || meta.body}</p>
        </div>

        {resumeAdventureId && (
          <div
            data-testid="resume-bookmark-banner"
            className="rounded-sm p-4 border border-emerald-500/40 bg-emerald-500/5 flex items-start gap-3"
          >
            <span className="gold text-lg leading-none mt-0.5">⤷</span>
            <div className="flex-1">
              <div className="font-mono text-[0.6rem] uppercase tracking-[0.35em] gold">Bookmarked</div>
              <div className="font-serif-body text-stone-200 text-sm mt-0.5">
                Your chronicle is saved. Ascend now and you will land back inside it the moment the rite completes.
              </div>
            </div>
          </div>
        )}

        {founder?.active && (
          <div
            data-testid="founder-banner"
            className="relative rounded-sm p-4 border border-amber-500 bg-gradient-to-br from-amber-500/20 via-amber-500/5 to-transparent overflow-hidden"
          >
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <div className="font-mono text-[0.6rem] uppercase tracking-[0.4em] gold flex items-center gap-2">
                  <span className="text-lg leading-none">✦</span> Founding Sovereign — Charter Offer
                </div>
                <div className="font-serif-display text-2xl mt-1">$49 lifetime · Only {founder.remaining} of {founder.total_slots} left</div>
                <div className="font-serif-body italic text-stone-300 text-sm mt-1">
                  Lifetime access, an eternal <span className="gold">◆ Founding Sovereign</span> badge, and your worlds forever marked in the archive.
                </div>
              </div>
              <button
                data-testid="claim-founder-btn"
                onClick={claimFounder}
                disabled={loading}
                className="btn-gold"
              >
                {loading ? "..." : "Claim Charter"}
              </button>
            </div>
            <div className="mt-3 h-1 bg-stone-800 rounded-sm overflow-hidden">
              <div className="h-full bg-gradient-to-r from-amber-400 to-amber-600" style={{ width: `${(founder.claimed / founder.total_slots) * 100}%` }} />
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {tiers.map((t) => (
            <button
              key={t.id}
              data-testid={`tier-${t.id}`}
              onClick={() => setSelected(t.id)}
              className={`relative text-left rounded-sm p-4 border transition-all ${
                selected === t.id
                  ? "border-amber-500 bg-amber-500/10 shadow-[0_0_20px_-8px_rgba(212,175,55,0.5)]"
                  : "border-stone-700 hover:border-amber-500/50"
              }`}
            >
              {t.highlight && (
                <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-amber-500 text-stone-950 font-mono text-[0.55rem] uppercase tracking-widest rounded-sm">
                  ★ Best Value
                </div>
              )}
              <div className="font-serif-display text-base tracking-widest uppercase">{t.label}</div>
              <div className="font-serif-display text-2xl gold mt-2">{t.price_display}</div>
              <div className="font-mono text-[0.6rem] uppercase tracking-widest text-stone-500 mt-1">{t.byline}</div>
              <div className={`mt-3 h-1 w-full ${selected === t.id ? "bg-amber-500" : "bg-stone-800"} transition-colors`} />
            </button>
          ))}
        </div>

        <div className="border-t border-b border-amber-500/20 py-4 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
          <BenefitRow>All handcrafted worlds unlocked</BenefitRow>
          <BenefitRow>Hard Mode with permadeath</BenefitRow>
          <BenefitRow>Unlimited chronicles & versions</BenefitRow>
          <BenefitRow>Publish worlds to the community</BenefitRow>
          <BenefitRow>Play & rate community worlds</BenefitRow>
          <BenefitRow>Priority support</BenefitRow>
        </div>

        <div className="flex items-center justify-end gap-3 flex-wrap">
          <button data-testid="paywall-close" onClick={onClose} className="btn-ghost" disabled={loading}>Not yet</button>
          <button data-testid="paywall-upgrade" onClick={ascend} disabled={loading || tiers.length === 0} className="btn-gold">
            {loading ? "Opening..." : "Ascend · Continue"}
          </button>
        </div>
        <div className="font-mono text-[0.6rem] uppercase tracking-widest text-stone-600 text-center">
          Secure checkout · Stripe · Test card 4242 4242 4242 4242
        </div>
      </div>
    </div>
  );
}

function BenefitRow({ children }) {
  return (
    <div className="flex items-start gap-2 font-serif-body text-stone-200 text-sm">
      <span className="gold mt-0.5">◆</span>
      <span>{children}</span>
    </div>
  );
}
